import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import logo from '../logo.webp';
import logoSmall from '../logo-small.webp';
import { useCart } from '../context/CartContext';
import { LANGUAGE_CODES, LANGUAGES } from '../i18n/languages';

export default function Navbar() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { language, setLanguage, localizePath, t } = useLanguage();
  const { items } = useCart();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <>
      <nav className="navbar">
        <Link to={localizePath('/')} className="nav-logo">
          <img
            src={logoSmall}
            srcSet={`${logoSmall} 96w, ${logo} 551w`}
            sizes="36px"
            alt=""
            aria-hidden="true"
            className="nav-logo-img"
          />
          <span className="nav-logo-label">{t('navbar.brand')}</span>
        </Link>
        <div className="nav-links">
          <label className="nav-language-select">
            <span aria-hidden="true">🌐</span>
            <span className="sr-only">{t('navbar.language')}</span>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              aria-label={t('navbar.language')}
            >
              {LANGUAGE_CODES.map((code) => <option key={code} value={code}>{LANGUAGES[code].label}</option>)}
            </select>
          </label>
          <label className="nav-language-select nav-language-select--mobile" title={t('navbar.language')}>
            <span aria-hidden="true">🌐</span>
            <span className="sr-only">{t('navbar.language')}</span>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              aria-label={t('navbar.language')}
            >
              {LANGUAGE_CODES.map((code) => <option key={code} value={code}>{LANGUAGES[code].shortLabel}</option>)}
            </select>
          </label>
          <a
            href="https://wa.me/13054910131"
            target="_blank"
            rel="noreferrer"
            aria-label={t('navbar.chatOnWhatsApp')}
            title={t('navbar.chatOnWhatsApp')}
            className="nav-whatsapp"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2a9.8 9.8 0 0 0-8.43 14.78L2 22l5.38-1.51A9.8 9.8 0 1 0 12 2Zm0 17.84a8.02 8.02 0 0 1-4.09-1.12l-.29-.17-3.19.9.86-3.1-.19-.31A8.03 8.03 0 1 1 12 19.84Zm4.4-6.05c-.24-.12-1.42-.7-1.64-.78-.22-.08-.38-.12-.54.12-.16.24-.62.78-.76.94-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.92-1.19-.71-.63-1.19-1.4-1.33-1.64-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.24 1.01.38 1.36.48.57.18 1.09.15 1.5.09.46-.07 1.42-.58 1.62-1.14.2-.56.2-1.03.14-1.14-.06-.11-.22-.18-.46-.3Z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/sunshine_intl_services/"
            target="_blank"
            rel="noreferrer"
            aria-label={t('navbar.viewInstagram')}
            title={t('navbar.viewInstagram')}
            className="nav-instagram"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9a5.5 5.5 0 0 1-5.5 5.5h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 1.8A3.7 3.7 0 0 0 3.8 7.5v9a3.7 3.7 0 0 0 3.7 3.7h9a3.7 3.7 0 0 0 3.7-3.7v-9a3.7 3.7 0 0 0-3.7-3.7h-9Zm9.45 1.35a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z" />
            </svg>
          </a>
          <a
            href="https://www.facebook.com/toursandtravel1/"
            target="_blank"
            rel="noreferrer"
            aria-label={t('navbar.viewFacebook')}
            title={t('navbar.viewFacebook')}
            className="nav-facebook"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.7V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.2v2.2H8V14h2.4v8h3.1Z" />
            </svg>
          </a>
          <a
            href="https://www.youtube.com/@Miamitoursandtravel"
            target="_blank"
            rel="noreferrer"
            aria-label={t('navbar.viewYouTube')}
            title={t('navbar.viewYouTube')}
            className="nav-youtube"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M21.58 7.19a2.98 2.98 0 0 0-2.1-2.11C17.63 4.58 12 4.58 12 4.58s-5.63 0-7.48.5a2.98 2.98 0 0 0-2.1 2.11A31.2 31.2 0 0 0 1.92 12c0 1.62.21 3.24.5 4.81a2.98 2.98 0 0 0 2.1 2.11c1.85.5 7.48.5 7.48.5s5.63 0 7.48-.5a2.98 2.98 0 0 0 2.1-2.11c.29-1.57.5-3.19.5-4.81 0-1.62-.21-3.24-.5-4.81ZM9.99 15.15V8.85L15.25 12l-5.26 3.15Z" />
            </svg>
          </a>
          <Link to={localizePath('/tours')}>{t('navbar.explore')}</Link>
          <Link to={localizePath('/guides')}>{t('navbar.guides')}</Link>
          <Link to={localizePath('/contact')}>{t('navbar.contact')}</Link>
          <Link to={localizePath('/cart')} className="nav-cart" aria-label={t('cart.ariaLabel', { count: items.length })}>
            🛒 <span>{t('cart.navLabel')}</span>{items.length > 0 && <b>{items.length}</b>}
          </Link>
          <Link to={localizePath('/tours')} className="nav-cta">{t('navbar.bookTour')}</Link>
          <button
            type="button"
            className={`nav-menu-toggle ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen((current) => !current)}
            aria-label={menuOpen ? t('navbar.closeMenu') : t('navbar.openMenu')}
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <div
        className={`mobile-nav-overlay ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />
      <div className={`mobile-nav-drawer ${menuOpen ? 'open' : ''}`}>
        <div className="mobile-nav-panel">
          <Link to={localizePath('/tours')} className="mobile-nav-link">
            {t('navbar.exploreTours')}
          </Link>
          <Link to={localizePath('/transportation')} className="mobile-nav-link">
            {t('navbar.transportation')}
          </Link>
          <Link to={localizePath('/luggage-storage')} className="mobile-nav-link">
            {t('navbar.luggageStorage')}
          </Link>
          <Link to={localizePath('/guides')} className="mobile-nav-link">
            {t('navbar.guides')}
          </Link>
          <Link to={localizePath('/book')} className="mobile-nav-link">
            {t('navbar.bookNow')}
          </Link>
          <Link to={localizePath('/cart')} className="mobile-nav-link">🛒 {t('cart.navLabel')} ({items.length})</Link>
          <Link to={localizePath('/contact')} className="mobile-nav-link">
            {t('navbar.contactUs')}
          </Link>
          <a
            href="https://g.page/r/CXXpjbmPOif2EBM/review"
            target="_blank"
            rel="noreferrer"
            className="mobile-nav-link"
          >
            {t('navbar.reviewUs')}
          </a>
          <div className="mobile-nav-socials">
            <a
              href="https://wa.me/13054910131"
              target="_blank"
              rel="noreferrer"
              aria-label={t('navbar.chatOnWhatsApp')}
              title={t('navbar.chatOnWhatsApp')}
              className="mobile-nav-social-button mobile-nav-social-button--whatsapp"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 2a9.8 9.8 0 0 0-8.43 14.78L2 22l5.38-1.51A9.8 9.8 0 1 0 12 2Zm0 17.84a8.02 8.02 0 0 1-4.09-1.12l-.29-.17-3.19.9.86-3.1-.19-.31A8.03 8.03 0 1 1 12 19.84Zm4.4-6.05c-.24-.12-1.42-.7-1.64-.78-.22-.08-.38-.12-.54.12-.16.24-.62.78-.76.94-.14.16-.28.18-.52.06-.24-.12-1.01-.37-1.92-1.19-.71-.63-1.19-1.4-1.33-1.64-.14-.24-.01-.37.11-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.41-.54-.42h-.46c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2s.86 2.32.98 2.48c.12.16 1.69 2.58 4.1 3.62.57.24 1.01.38 1.36.48.57.18 1.09.15 1.5.09.46-.07 1.42-.58 1.62-1.14.2-.56.2-1.03.14-1.14-.06-.11-.22-.18-.46-.3Z" />
              </svg>
            </a>
            <a
              href="https://www.instagram.com/sunshine_intl_services/"
              target="_blank"
              rel="noreferrer"
              aria-label={t('navbar.viewInstagram')}
              title={t('navbar.viewInstagram')}
              className="mobile-nav-social-button mobile-nav-social-button--instagram"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9a5.5 5.5 0 0 1-5.5 5.5h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2Zm0 1.8A3.7 3.7 0 0 0 3.8 7.5v9a3.7 3.7 0 0 0 3.7 3.7h9a3.7 3.7 0 0 0 3.7-3.7v-9a3.7 3.7 0 0 0-3.7-3.7h-9Zm9.45 1.35a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4ZM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10Zm0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8Z" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/toursandtravel1/"
              target="_blank"
              rel="noreferrer"
              aria-label={t('navbar.viewFacebook')}
              title={t('navbar.viewFacebook')}
              className="mobile-nav-social-button mobile-nav-social-button--facebook"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.6-1.5h1.7V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.2v2.2H8V14h2.4v8h3.1Z" />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@Miamitoursandtravel"
              target="_blank"
              rel="noreferrer"
              aria-label={t('navbar.viewYouTube')}
              title={t('navbar.viewYouTube')}
              className="mobile-nav-social-button mobile-nav-social-button--youtube"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21.58 7.19a2.98 2.98 0 0 0-2.1-2.11C17.63 4.58 12 4.58 12 4.58s-5.63 0-7.48.5a2.98 2.98 0 0 0-2.1 2.11A31.2 31.2 0 0 0 1.92 12c0 1.62.21 3.24.5 4.81a2.98 2.98 0 0 0 2.1 2.11c1.85.5 7.48.5 7.48.5s5.63 0 7.48-.5a2.98 2.98 0 0 0 2.1-2.11c.29-1.57.5-3.19.5-4.81 0-1.62-.21-3.24-.5-4.81ZM9.99 15.15V8.85L15.25 12l-5.26 3.15Z" />
              </svg>
            </a>
            <a
              href="tel:+13054910131"
              aria-label={t('navbar.call')}
              title={t('navbar.call')}
              className="mobile-nav-social-button mobile-nav-social-button--call"
            >
              <span aria-hidden="true">📞</span>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
